import { normalizeId } from '@studio/models/identifier';
import type { RoomRecord } from '@studio/models/room';
/* eslint-disable import/no-internal-modules */
import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { useAnalytics } from '#~/analytics/index.js';
import { getActiveStudioId, subscribeActiveStudio } from '#~/analytics/active-studio.js';
import { usePlanPurchases } from '#~/api/billing/use-plan-purchase.js';
import { hasUnresolvedFuturePurchase, matchesPlanPurchase } from '#~/api/billing/plan-purchase.js';
import type { PlanPurchaseView } from '#~/api/billing/plan-purchase.js';
import { forceRefreshBilling } from '#~/api/billing/limits.js';
import { toIdFromHex } from '#~/api/utils.js';
import { useAuth0 } from '#~/auth/use-auth0.js';
import { useVerifiedAction } from '#~/auth/email-verification-gate/use-verified-action.jsx';
import { config } from '#~/config/index.js';
import { useDepot } from '#~/depot/hooks.js';
import { depot } from '#~/depot/index.js';
import { ElectronAPI } from '#~/electron/index.js';
import { isElectronApp } from '#~/electron/utils.js';
import { logger } from '#~/logger/index.js';
import { nats } from '#~/nats/index.js';
import { post } from '#~/notifications/utils.js';
import { useLastRoomIdPref } from '#~/room/hook_last_id.js';
import { Paths } from '#~/routes/constants.js';
import { DataSTag, Sel } from '#~/stag/index.js';
import { Button } from '@studio/design-system/button';
import { Callout } from '@studio/design-system/callout';
import { Spinner } from '@studio/design-system/spinner';
import * as Dialog from '@studio/design-system/dialog';
import { usePrompt } from '@studio/design-system/prompt';
import { dayjs } from '@studio/js-utils/time';
import { AgentRegistryMembershipRole as Role } from '@tryiris-ai/common.protojs/api/v1/agent_registry';
import {
  BillingRegistryChangeRoomPlanRequest,
  BillingRegistryGetRoomBillingInfoRequest,
  BillingRegistryGetRoomBillingInfoResponse,
  BillingRegistryGetRoomBillingPortalURLRequest,
  BillingRegistryPlan,
  BillingRegistryPlanFrequency,
  BillingRegistryPlanPurchaseState as PurchaseState
} from '@tryiris-ai/common.protojs/api/v1/billing_registry';
import {
  BillingRegistryChangePlan,
  BillingRegistryGetBillingInfo,
  BillingRegistryGetbillingPortal
} from '@tryiris-ai/mailroom-helper/billing';
import { floor } from 'lodash-es';
import type { ReactElement } from 'react';
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';
import { classifyBillingResponse, thrownRequestMessage } from './billing-response-helpers.js';
import { ChooseYourPlans } from './choose-your-plan.jsx';
import { SendContactSalesRequest } from './contact-sales.js';
import { formatOveragePrice } from './format-overage-price.js';
import { FuturePlanPurchaseSummary, futurePurchaseDate } from './future-plan-purchase-summary.jsx';
import { InvoicesSection } from './invoices.jsx';
import { standardTrialEndDate, standardTrialLabel } from './standard-trial-label.js';
import { PaymentMethodCard } from './payment-method.jsx';
import type { BillingInfo, Props } from './types.js';
import { UpcomingInvoiceCard } from './upcoming-invoice.jsx';
import { CloudUsage } from './usage.jsx';
import type { User } from '@studio/models/user';
import type { Room } from '@studio/models/room';

// @ts-expect-error
const data: BillingInfo = {
  studio: {
    name: 'Blue Note Tokyo'
  },
  //isOverdue: true,
  invoices: [
    /*
    {
      id: '67CDB9FB-00012',
      plan: 'pro',
      period: dayjs(new Date()).startOf('month').toDate(),
      total: {
        value: 25,
        currency: '$'
      },
      status: 'upcoming'
    },
    {
      id: '67CDB9FB-00008',
      plan: 'standard',
      period: dayjs(new Date()).subtract(1, 'month').startOf('month').toDate(),
      total: {
        value: 25,
        currency: '$'
      },
      status: 'overdue'
    },
    {
      id: '67CDB9FB-00013',
      plan: 'standard',
      period: dayjs(new Date()).subtract(2, 'month').startOf('month').toDate(),
      total: {
        value: 25,
        currency: '$'
      },
      status: 'paid'
    },

    {
      id: '67CDB9FB-00014',
      plan: 'standard',
      period: dayjs(new Date()).subtract(3, 'month').startOf('month').toDate(),
      total: {
        value: 25,
        currency: '$'
      },
      status: 'paid'
    }
    */
  ],
  usage: {
    quotaUsed: 2000,
    quotaIncluded: 10000
  }
};

/** `<Billing>` panel. */
export const Billing = ({
  initialPlanPickerRoomId,
  onInitialPlanPickerOpened,
  ...props
}: Props): ReactElement => {
  const rooms = useDepot((v) => v.roomRecords);
  const actorId = useDepot((v) => v.user?.id);
  const [open, setOpen] = useState(false);
  const pickerOpen = useRef(open);
  pickerOpen.current = open;
  const { getAccessTokenSilently } = useAuth0();
  const generate = getAccessTokenSilently;
  const [lastRoomID] = useLastRoomIdPref();
  const activeRoomId = useSyncExternalStore(subscribeActiveStudio, getActiveStudioId);
  const selectedRoomId = activeRoomId ?? lastRoomID ?? '';
  const findRoom = (id: string) => {
    const key = normalizeId<RoomRecord['id']>(id);
    return key === undefined ? undefined : rooms[key];
  };
  const selectedRoom = findRoom(selectedRoomId);
  const selectedId = useRef(selectedRoomId);
  selectedId.current = selectedRoomId;
  const { track } = useAnalytics();
  const navigate = useNavigate();
  const { store, purchases, actorKey, isCurrentAccount } = usePlanPurchases();
  const billingMounted = useRef(true);
  const payRequest = useRef<string | undefined>(undefined);
  const [payingPurchase, setPayingPurchase] = useState<string | undefined>();
  const [purchaseActionError, setPurchaseActionError] = useState<{
    key: string;
    message: string;
  }>();
  const futurePurchase = (purchases[selectedRoomId]?.receipt?.effectiveAt ?? 0) > 0;
  const pendingFuture = hasUnresolvedFuturePurchase(purchases[selectedRoomId]);
  const currentOwner =
    isCurrentAccount() &&
    selectedRoom?.permissions?.role === Role.AGENT_REGISTRY_MEMBERSHIP_ROLE_OWNER;
  const [billingRecord, setBillingRecord] = useState<{
    roomId: string;
    actorKey: string;
    info: BillingRegistryGetRoomBillingInfoResponse;
  }>();
  // Even before an effect can cancel, the previous Studio's response must not
  // render as the newly selected Studio's plan.
  const billingInfo =
    billingRecord?.roomId === selectedRoomId &&
    billingRecord.actorKey === actorKey &&
    isCurrentAccount()
      ? billingRecord.info
      : undefined;
  const [refreshCounter, setRefreshCounter] = useState(0);
  const selecting = useRef(false);
  const reviewedQuote = useRef<{ roomId: string; ownerId: string; quoteId: string } | undefined>(
    undefined
  );
  const quoteRequestGeneration = useRef(0);
  const [quoteRevision, setQuoteRevision] = useState(0);
  const ownsCurrentStudio = (roomId: Room['id'], ownerId: User['id'] | undefined): boolean =>
    pickerOpen.current &&
    isCurrentAccount() &&
    ownerId !== undefined &&
    depot.getState().user?.id === ownerId &&
    selectedId.current === roomId &&
    findRoom(roomId)?.permissions?.role === Role.AGENT_REGISTRY_MEMBERSHIP_ROLE_OWNER &&
    depot.getState().roomRecords[normalizeId<RoomRecord['id']>(roomId)!]?.permissions?.role ===
      Role.AGENT_REGISTRY_MEMBERSHIP_ROLE_OWNER;
  const ownsCurrentPurchase = (purchase: PlanPurchaseView): boolean => {
    const current = store?.snapshot()[purchase.roomId];
    const id = normalizeId<RoomRecord['id']>(purchase.roomId);
    return (
      billingMounted.current &&
      isCurrentAccount() &&
      selectedId.current === purchase.roomId &&
      id !== undefined &&
      depot.getState().roomRecords[id]?.permissions?.role ===
        Role.AGENT_REGISTRY_MEMBERSHIP_ROLE_OWNER &&
      current?.sessionId === purchase.sessionId &&
      current.receipt !== undefined &&
      purchase.receipt !== undefined &&
      matchesPlanPurchase(purchase.receipt, current.receipt)
    );
  };
  const initialPickerHandled = useRef<string | undefined>(undefined);

  const prompt = usePrompt();
  // Plan changes and the billing portal need a verified email; viewing billing and contacting sales do not.
  const verifiedAction = useVerifiedAction('billing');

  useEffect(() => {
    const interval = setInterval(() => setRefreshCounter((count) => count + 1), 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setRefreshCounter((c) => c + 1);
      }
    };

    const handleFocus = () => {
      setRefreshCounter((c) => c + 1);
    };

    // Add listeners for both events
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    // Cleanup both listeners on unmount
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const planMap = {
    [BillingRegistryPlan.PLAN_FREE]: 'Free',
    [BillingRegistryPlan.PLAN_STANDARD]: 'Standard',
    [BillingRegistryPlan.PLAN_PRO]: 'Pro',
    [BillingRegistryPlan.PLAN_ENTERPRISE]: 'Enterprise',
    [BillingRegistryPlan.PLAN_CUSTOM]: 'Custom',
    [BillingRegistryPlan.PLAN_NFR]: 'Not For Resale (NFR)',
    // PLAN_UNKNOWN (=6) was added to billing_registry.proto but was absent from
    // the stale registry-published protojs the frontend built against. Now that
    // consumers build off the in-repo single-source proto (this unit), the enum
    // is exhaustive and every key must be mapped. Renders as "Unknown".
    [BillingRegistryPlan.PLAN_UNKNOWN]: 'Unknown'
  };

  useEffect(() => {
    let canceled = false;
    const roomId = selectedRoomId;
    const load = async () => {
      if (roomId === '' || actorKey === undefined || !isCurrentAccount()) return;
      try {
        const token = await generate();
        if (canceled || !isCurrentAccount()) return;
        const resp = await BillingRegistryGetBillingInfo.request(
          nats.cloudConnection(),
          BillingRegistryGetRoomBillingInfoRequest.fromPartial({
            roomID: toIdFromHex(roomId)!,
            token
          }),
          // Billing must come from the current account, including after a same-Studio sign-in change.
          { force: true, cacheTTL: 0, staleOnError: false }
        );
        if (canceled || !isCurrentAccount()) return;
        if (resp.error !== undefined) {
          setBillingRecord(undefined);
          return;
        }
        setBillingRecord({ roomId, actorKey, info: resp });
        if (resp.purchase !== undefined && store !== undefined) {
          store.register(roomId, resp.purchase, 'billing');
          void store.resume(roomId);
        }
        await forceRefreshBilling(roomId, isCurrentAccount);
      } catch (error) {
        if (!canceled && isCurrentAccount()) setBillingRecord(undefined);
        logger.error('billing information unavailable', error);
      }
    };
    void load();
    return () => {
      canceled = true;
    };
  }, [selectedRoomId, actorKey, isCurrentAccount, refreshCounter, store, initialPlanPickerRoomId]);

  useEffect(() => {
    if (store === undefined) return;
    for (const purchase of Object.values(purchases)) {
      if (purchase.status !== 'ready' || !store.consumeReady(purchase.roomId)) continue;
      const plan =
        purchase.observedPlan?.plan ?? purchase.receipt?.plan ?? purchase.portalQuote?.plan;
      const frequency =
        purchase.observedPlan?.frequency ??
        purchase.receipt?.frequency ??
        purchase.portalQuote?.frequency;
      if (plan === undefined || frequency === undefined) continue;
      const studioName = findRoom(purchase.roomId)?.name ?? 'the purchased Studio';
      track(
        frequency === BillingRegistryPlanFrequency.PLAN_FREQUENCY_YEARLY
          ? EVENT_NAMES.SUCCESSFULLY_SUBSCRIBE_ANNUAL_PLAN
          : EVENT_NAMES.SUCCESSFULLY_SUBSCRIBE_PAID_PLAN,
        EVENT_CATEGORIES.BILLING,
        {
          planType: planMap[plan].toLowerCase(),
          billingFrequency:
            frequency === BillingRegistryPlanFrequency.PLAN_FREQUENCY_YEARLY ? 'yearly' : 'monthly',
          studio_id: purchase.roomId
        }
      );
      post(
        {
          title: 'Plan Successfully Changed',
          message: `${studioName} now has the ${planMap[plan]} plan.`,
          severity: 'success'
        },
        { autoClose: false }
      );
      if (purchase.roomId === selectedRoomId) setRefreshCounter((count) => count + 1);
    }
  }, [purchases, store, selectedRoomId, rooms, track]);

  useEffect(() => {
    quoteRequestGeneration.current++;
    reviewedQuote.current = undefined;
    initialPickerHandled.current = undefined;
    setOpen(false);
  }, [selectedRoomId, actorKey, actorId, pendingFuture]);
  useEffect(() => {
    billingMounted.current = true;
    return () => {
      billingMounted.current = false;
      pickerOpen.current = false;
      quoteRequestGeneration.current++;
      reviewedQuote.current = undefined;
    };
  }, []);

  const cancelPurchase = (purchase: PlanPurchaseView) => {
    if (
      !ownsCurrentPurchase(purchase) ||
      purchase.receipt?.canCancel !== true ||
      purchase.cancellationRequested
    )
      return;
    const end = futurePurchaseDate(billingInfo?.trialEnd ?? 0);
    prompt.confirm({
      title: 'Cancel your future purchase?',
      message: `${end === undefined ? 'Your current access will not be changed by this request.' : `Your Standard trial will continue until ${end}.`} Cancellation is complete only after it is confirmed. If payment has already been collected, the paid result will be shown instead.`,
      okay: {
        ...DataSTag(Sel.AccountSettings, 'purchase-cancel-confirm', purchase.roomId),
        label: 'Cancel future purchase',
        variant: 'primary'
      },
      cancel: { label: 'Keep purchase' },
      onConfirm: (answer) => {
        if (!answer || !ownsCurrentPurchase(purchase)) return;
        void store?.requestCancellation(purchase.roomId, purchase.sessionId).then(() => {
          if (ownsCurrentPurchase(purchase)) setRefreshCounter((count) => count + 1);
        });
      }
    });
  };

  const payExistingInvoice = async (purchase: PlanPurchaseView) => {
    const key = JSON.stringify([actorKey, purchase.roomId, purchase.sessionId]);
    if (!ownsCurrentPurchase(purchase) || payRequest.current === key) return;
    await verifiedAction.run(async () => {
      if (!ownsCurrentPurchase(purchase) || payRequest.current === key) return;
      payRequest.current = key;
      setPayingPurchase(key);
      setPurchaseActionError(undefined);
      try {
        // A cached invoice URL cannot survive an unavailable, canceled or already-paid status read.
        await store?.resume(purchase.roomId);
        if (!ownsCurrentPurchase(purchase)) return;
        const fresh = store?.snapshot()[purchase.roomId];
        if (
          fresh?.status === 'canceled' ||
          fresh?.status === 'ready' ||
          fresh?.status === 'confirmed'
        )
          return;
        if (
          fresh?.status !== 'payment-required' ||
          fresh.receiptVerified !== true ||
          fresh.receipt === undefined
        )
          throw new Error('existing invoice status unavailable');
        const url = new URL(fresh.receipt.paymentUrl);
        if (
          url.protocol !== 'https:' ||
          url.host !== 'invoice.stripe.com' ||
          url.username !== '' ||
          url.password !== ''
        )
          throw new Error('existing invoice link unavailable');
        ElectronAPI.openUrl(url.href, false);
      } catch {
        if (ownsCurrentPurchase(purchase))
          setPurchaseActionError({
            key,
            message:
              'The existing invoice could not be confirmed. Check purchase status and try again.'
          });
      } finally {
        if (payRequest.current === key) {
          payRequest.current = undefined;
          if (billingMounted.current) setPayingPurchase(undefined);
        }
      }
    });
  };

  const billing = useMemo(
    () =>
      ({
        ...data,
        studio: {
          name: selectedRoom?.name ?? ''
        },
        activePlan: {
          planHuman: planMap[billingInfo?.plan || 0] ?? 'Custom',
          planSystem: (planMap[billingInfo?.plan || 0] ?? 'Custom').toLocaleLowerCase(),
          type:
            billingInfo?.frequency === BillingRegistryPlanFrequency.PLAN_FREQUENCY_MONTHLY
              ? 'monthly'
              : 'yearly',
          includedUsage: billingInfo?.planIncludedUsage ?? 0,
          // Per ENG-3727: use formatOveragePrice so 0 renders as "$0.00", not "Unknown".
          overagePrice: formatOveragePrice(billingInfo?.planOverageRate),
          // undefined for every non-trial Studio, so nothing else changes.
          trialEndsAt: standardTrialEndDate(billingInfo?.trialEnd)
        },

        nextPlan: billingInfo?.willDowngradeOnTermEnd
          ? {
              planHuman: planMap[billingInfo?.nextInvoicePlan || 0] ?? 'Custom',
              willDowngrade: !!billingInfo?.willDowngradeOnTermEnd,
              planDate: billingInfo?.billingTermEnd
                ? dayjs((billingInfo?.billingTermEnd ?? 0) * 1000).toDate()
                : new Date()
            }
          : undefined,

        usage: {
          quotaUsed: billingInfo?.usage ?? 0,
          quotaIncluded: billingInfo?.planIncludedUsage ?? 0
        },
        upcomingBilling: {
          from: billingInfo?.billingTermStart
            ? dayjs((billingInfo?.billingTermStart ?? 0) * 1000).toDate()
            : undefined,
          to: billingInfo?.billingTermEnd
            ? dayjs((billingInfo?.billingTermEnd ?? 0) * 1000).toDate()
            : undefined,
          amount: billingInfo?.nextInvoiceAmount ?? 0,
          currencyPrefix: '$'
        },

        loaded: billingInfo !== undefined,

        invoices:
          billingInfo?.invoices?.map((invoice) => ({
            id: invoice.id,
            plan: planMap[invoice.plan] ?? 'Custom',
            period: dayjs((invoice.datetime ?? 0) * 1000).toDate(),
            status: invoice.status,
            total: {
              value: floor(invoice.amount).toFixed(2),
              currency: '$'
            },
            url: invoice.url
          })) ?? []
      }) satisfies BillingInfo,
    [selectedRoom?.name, billingInfo]
  );

  useEffect(() => {
    const requested = normalizeId<RoomRecord['id']>(initialPlanPickerRoomId ?? '');
    // Reuse the identity-fenced billing record and picker: navigation, absence
    // or a response from the previous Studio cannot open purchase controls.
    if (
      requested === undefined ||
      requested !== selectedRoomId ||
      !billing.loaded ||
      pendingFuture ||
      initialPickerHandled.current === requested
    )
      return;
    initialPickerHandled.current = requested;
    setOpen(true);
    onInitialPlanPickerOpened?.(requested);
  }, [
    initialPlanPickerRoomId,
    onInitialPlanPickerOpened,
    selectedRoomId,
    billing.loaded,
    pendingFuture
  ]);

  return (
    <div {...props}>
      <div className='flex flex-col gap-32'>
        {Object.values(purchases)
          .filter(
            (purchase) =>
              purchase.status !== 'unconfirmed' &&
              purchase.status !== 'ready' &&
              ((purchase.receipt?.effectiveAt ?? 0) <= 0 ||
                (purchase.roomId === selectedRoomId && findRoom(purchase.roomId) !== undefined))
          )
          .map((purchase) => {
            const future = (purchase.receipt?.effectiveAt ?? 0) > 0;
            const actionKey = JSON.stringify([actorKey, purchase.roomId, purchase.sessionId]);
            const canceling =
              purchase.cancellationRequested === true || purchase.status === 'canceling';
            // Future purchase actions require the current Studio owner's role.
            const canManagePurchase =
              !future ||
              findRoom(purchase.roomId)?.permissions?.role ===
                Role.AGENT_REGISTRY_MEMBERSHIP_ROLE_OWNER;
            return (
              <Callout
                key={purchase.roomId}
                severity='info'
                {...DataSTag(Sel.AccountSettings, 'plan-purchase-status', purchase.roomId)}
                title={
                  <div>
                    <div>
                      {findRoom(purchase.roomId)?.name ?? 'Purchased Studio'}:{' '}
                      {future ? (
                        <FuturePlanPurchaseSummary
                          purchase={purchase}
                          currentPlan={billingInfo?.plan}
                          currentTrialEnd={billingInfo?.trialEnd}
                        />
                      ) : purchase.status === 'open' ? (
                        'Checkout is open. Your current access continues until payment is confirmed.'
                      ) : purchase.status === 'expired' ? (
                        purchase.receipt?.reason === 'purchased_subscription_ended' ? (
                          'That purchase is no longer active.'
                        ) : (
                          'Checkout ended without confirmed payment.'
                        )
                      ) : purchase.status === 'confirmed' ||
                        purchase.status === 'payment-received' ? (
                        'Payment confirmed. Refreshing this Studio’s access.'
                      ) : purchase.status === 'unavailable' ? (
                        'Payment status is unavailable. Check the same purchase again.'
                      ) : (
                        'Checking payment confirmation.'
                      )}
                    </div>
                    <div className='mt-12 flex flex-wrap gap-12'>
                      {future &&
                        canManagePurchase &&
                        (canceling ||
                          (purchase.receiptVerified === true && purchase.receipt?.canCancel)) && (
                          <Button
                            {...DataSTag(Sel.AccountSettings, 'purchase-cancel', purchase.roomId)}
                            variant='secondary'
                            disabled={canceling}
                            aria-busy={canceling}
                            className='focus-visible:ring-primary focus-visible:outline-none focus-visible:ring-2'
                            onClick={() => cancelPurchase(purchase)}
                          >
                            {canceling && <Spinner className='mr-8' />}
                            {canceling ? 'Canceling purchase' : 'Cancel future purchase'}
                          </Button>
                        )}
                      {future && canManagePurchase && purchase.status === 'payment-required' && (
                        <Button
                          {...DataSTag(Sel.AccountSettings, 'purchase-pay', purchase.roomId)}
                          disabled={
                            payingPurchase === actionKey ||
                            purchase.receiptVerified !== true ||
                            !purchase.receipt?.paymentUrl
                          }
                          aria-busy={payingPurchase === actionKey}
                          className='focus-visible:ring-primary focus-visible:outline-none focus-visible:ring-2'
                          onClick={() => void payExistingInvoice(purchase)}
                        >
                          {payingPurchase === actionKey && <Spinner className='mr-8' />}
                          Pay existing invoice
                        </Button>
                      )}
                      {purchase.status !== 'ready' &&
                        purchase.status !== 'expired' &&
                        purchase.status !== 'canceled' &&
                        purchase.status !== 'failed' &&
                        canManagePurchase && (
                          <Button
                            {...DataSTag(Sel.AccountSettings, 'purchase-check', purchase.roomId)}
                            variant='secondary'
                            onClick={() => {
                              void store?.resume(purchase.roomId);
                            }}
                          >
                            {future ? 'Check purchase status' : 'Check payment status'}
                          </Button>
                        )}
                      {purchase.status === 'open' &&
                        canManagePurchase &&
                        purchase.receipt?.checkoutUrl !== '' &&
                        purchase.receipt?.checkoutUrl !== undefined && (
                          <Button
                            {...DataSTag(Sel.AccountSettings, 'purchase-continue', purchase.roomId)}
                            onClick={() => {
                              const url = purchase.receipt?.checkoutUrl;
                              if (url !== undefined && url !== '') ElectronAPI.openUrl(url, false);
                            }}
                          >
                            Continue checkout
                          </Button>
                        )}
                      {purchase.roomId !== selectedRoomId && (
                        <Button
                          {...DataSTag(
                            Sel.AccountSettings,
                            'purchase-view-studio',
                            purchase.roomId
                          )}
                          variant='secondary'
                          onClick={() => navigate(`/room/${purchase.roomId}`)}
                        >
                          View purchased Studio
                        </Button>
                      )}
                    </div>
                    {future && hasUnresolvedFuturePurchase(purchase) && canManagePurchase && (
                      <p className='mt-12 text-sm text-muted'>
                        Resolve or cancel this purchase before choosing another plan.
                      </p>
                    )}
                    {canManagePurchase && purchaseActionError?.key === actionKey && (
                      <p className='mt-12 text-sm' role='alert'>
                        {purchaseActionError.message}
                      </p>
                    )}
                  </div>
                }
              />
            );
          })}

        {billing.activePlan.trialEndsAt !== undefined && (
          // a live trial is the CURRENT plan, not a pending change,
          // so it gets its own notice rather than the plan-change callout
          // below (the backend reports no downgrade for a trialing Studio).
          <Callout
            severity='info'
            {...DataSTag(Sel.AccountSettings, 'standard-trial-notice')}
            title={
              <div className='text-sm font-regular'>
                {standardTrialLabel(billing.activePlan.trialEndsAt)}
              </div>
            }
          />
        )}
        {billing.nextPlan && (
          <Callout
            severity='info'
            title={
              <div className='text-sm font-regular'>
                Your plan will be changed on{' '}
                <b>{dayjs(billing.nextPlan.planDate).format('M/D/YYYY')}</b>. Your next estimated
                payment will be{' '}
                <b>
                  {billing.upcomingBilling.currencyPrefix}
                  {billing.upcomingBilling.amount.toFixed(2)}
                </b>
                .
              </div>
            }
          />
        )}
        <div className='flex gap-20'>
          <UpcomingInvoiceCard
            changeDisabled={pendingFuture}
            showChange={!futurePurchase || currentOwner}
            loading={!billing.loaded}
            billing={billing}
            className='flex-1'
            onCtaClick={async () => {
              if (hasUnresolvedFuturePurchase(store?.snapshot()[selectedRoomId])) return;
              // Track upgrade plan click event
              track(EVENT_NAMES.CLICK_UPGRADE_PLAN, EVENT_CATEGORIES.BILLING, {
                planType: billing.activePlan.planSystem
              });
              setOpen(true);
            }}
          />
          <PaymentMethodCard
            className='flex-1'
            loading={!billing.loaded}
            ctaType={
              billingInfo?.isPaymentMethodSet && billingInfo?.paymentMethodName ? 'edit' : 'add'
            }
            title={
              (billingInfo?.paymentMethodName?.length ?? 0) > 0
                ? billingInfo?.paymentMethodName
                : billing.activePlan.trialEndsAt !== undefined && billing.usage.quotaIncluded > 0
                  ? 'Add card for cloud overages'
                  : undefined
            }
            subline={
              (billingInfo?.paymentMethodExpAt?.length ?? 0) > 0
                ? billingInfo?.paymentMethodExpAt
                : billing.activePlan.trialEndsAt !== undefined && billing.usage.quotaIncluded > 0
                  ? 'Included cloud usage does not require a card. Saving a card does not buy a plan.'
                  : undefined
            }
            onCta={async () => {
              if (!isCurrentAccount()) return;
              await verifiedAction.run(async () => {
                if (!isCurrentAccount()) return;
                // Per ENG-3782: surface backend errors as a toast and KEEP
                // the dialog/CTA in a usable state. Pre-fix the request
                // could either throw (network/timeout) or return a
                // protobuf-error response with empty url, both leading to
                // a silent ElectronAPI.openUrl('', false) that does
                // nothing visible to the user. The two failure modes need
                // separate handling: try/catch for thrown rejections,
                // resp.error / !resp.url for protobuf-shaped errors.
                const req = BillingRegistryGetRoomBillingPortalURLRequest.fromPartial({
                  roomID: toIdFromHex(selectedRoomId)!,
                  returnUrl: `${config.frontendUrl}/room/${selectedRoomId}`,
                  token: await generate()
                });
                if (!isCurrentAccount()) return;

                try {
                  const resp = await BillingRegistryGetbillingPortal.request(
                    nats.cloudConnection(),
                    req
                  );
                  if (!isCurrentAccount()) return;

                  const decision = classifyBillingResponse(
                    resp,
                    'We could not open the billing portal. Please try again in a moment.'
                  );

                  if (decision.kind === 'error') {
                    post({
                      actionable: true,
                      title: 'Could not open billing portal',
                      message: decision.message,
                      severity: 'error'
                    });
                    return;
                  }

                  ElectronAPI.openUrl(decision.redirectUrl, false);
                } catch (err) {
                  if (!isCurrentAccount()) return;
                  logger.error('billing portal request rejected', err);
                  post({
                    actionable: true,
                    title: 'Could not open billing portal',
                    message: thrownRequestMessage(),
                    severity: 'error'
                  });
                }
              });
            }}
          />
        </div>

        <div>
          <CloudUsage
            canUpgrade={!futurePurchase || (currentOwner && !pendingFuture)}
            billing={billing}
            onUpgrade={() => {
              if (hasUnresolvedFuturePurchase(store?.snapshot()[selectedRoomId])) return;
              setOpen(true);
            }}
          />
        </div>
        {billing.loaded && billing.invoices?.length > 0 && (
          <div>
            <InvoicesSection
              billing={billing}
              onPayOverage={() => {
                // eslint-disable-next-line no-warning-comments
                // TODO: Implement this
              }}
            />
          </div>
        )}
      </div>
      <Dialog.Root
        open={open}
        onOpenChange={(next) => {
          pickerOpen.current = next;
          if (!next) {
            quoteRequestGeneration.current++;
            reviewedQuote.current = undefined;
          }
          setOpen(next);
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay />
          <Dialog.Content
            closeOnEscape
            className='fixed inset-0 overflow-auto bg-transparent py-20'
          >
            <ChooseYourPlans
              selectedRoomID={selectedRoomId}
              billing={billing}
              ownerKey={actorKey}
              quoteRevision={quoteRevision}
              canSchedule={
                isCurrentAccount() &&
                selectedRoom?.permissions?.role === Role.AGENT_REGISTRY_MEMBERSHIP_ROLE_OWNER
              }
              onCancelQuote={() => {
                quoteRequestGeneration.current++;
                reviewedQuote.current = undefined;
              }}
              onQuote={async (selection) => {
                const roomId = normalizeId<RoomRecord['id']>(selectedRoomId);
                const ownerId = depot.getState().user?.id;
                const generation = ++quoteRequestGeneration.current;
                // A superseded quote reply cannot replace a newer reviewed price's identity.
                const currentQuote = () =>
                  roomId !== undefined &&
                  generation === quoteRequestGeneration.current &&
                  !hasUnresolvedFuturePurchase(store?.snapshot()[roomId]) &&
                  ownsCurrentStudio(roomId, ownerId);
                reviewedQuote.current = undefined;
                if (roomId === undefined || !currentQuote())
                  throw new Error('trial quote owner context changed');
                const token = await generate();
                if (!currentQuote()) throw new Error('trial quote owner context changed');
                const response = await BillingRegistryChangePlan.request(
                  nats.cloudConnection(),
                  BillingRegistryChangeRoomPlanRequest.fromPartial({
                    roomID: toIdFromHex(roomId)!,
                    token,
                    quoteOnly: true,
                    plan:
                      selection.plan === 'pro'
                        ? BillingRegistryPlan.PLAN_PRO
                        : BillingRegistryPlan.PLAN_STANDARD,
                    frequency:
                      selection.type === 'yearly'
                        ? BillingRegistryPlanFrequency.PLAN_FREQUENCY_YEARLY
                        : BillingRegistryPlanFrequency.PLAN_FREQUENCY_MONTHLY,
                    expectedPriceId: selection.priceId,
                    expectedTrialEnd: selection.trialEnd,
                    promotionCode: selection.promotionCode
                  }),
                  { force: true, cacheTTL: 0, staleOnError: false }
                );
                if (!currentQuote()) throw new Error('trial quote owner context changed');
                if (
                  response.error !== undefined ||
                  response.purchase?.state !== PurchaseState.PURCHASE_STATE_QUOTED
                ) {
                  setRefreshCounter((count) => count + 1);
                  throw new Error('trial quote unavailable');
                }
                reviewedQuote.current = {
                  roomId,
                  ownerId: ownerId!,
                  quoteId: response.purchase.quoteId
                };
                return response.purchase;
              }}
              onClose={() => {
                pickerOpen.current = false;
                quoteRequestGeneration.current++;
                reviewedQuote.current = undefined;
                setOpen(false);
              }}
              onSelect={async ({ plan, type: frequency, purchaseConfirmed, quote }) => {
                const roomId = normalizeId<RoomRecord['id']>(selectedRoomId);
                if (roomId === undefined) return;
                const futureQuote = quote?.kind === 'future' ? quote.purchase : undefined;
                const ownerAtConsent = depot.getState().user?.id;
                // A deferred verification or old reply cannot open a canceled or superseded quote.
                const currentConsent = () =>
                  pickerOpen.current &&
                  isCurrentAccount() &&
                  selectedId.current === roomId &&
                  !hasUnresolvedFuturePurchase(store?.snapshot()[roomId]) &&
                  depot.getState().user?.id === ownerAtConsent &&
                  (futureQuote === undefined ||
                    (ownsCurrentStudio(roomId, ownerAtConsent) &&
                      reviewedQuote.current?.roomId === roomId &&
                      reviewedQuote.current.ownerId === ownerAtConsent &&
                      reviewedQuote.current.quoteId === futureQuote.quoteId));
                if (!currentConsent()) return;

                if (plan === 'enterprise') {
                  if (selecting.current === true) return;
                  selecting.current = true;
                  try {
                    const token = await generate();
                    if (!currentConsent()) return;

                    prompt.confirm({
                      title: 'Contact Sales',
                      className: 'pb-24',
                      message:
                        'Thank you for your interest. Our sales team will contact you to understand your specific needs and provide you with a custom pricing option.',
                      okay: {
                        label: 'Submit Request',
                        variant: 'primary'
                      },
                      cancel: {
                        label: 'Cancel'
                      },
                      onConfirm: (answer) => {
                        if (answer && currentConsent()) {
                          SendContactSalesRequest(roomId, token);
                        }
                      }
                    });
                  } finally {
                    selecting.current = false;
                  }
                  return;
                }

                // Per ENG-4603, a plan change needs a verified email; contacting sales
                // does not. A held change runs after verification, so it takes the
                // double-submit guard, mints its token, and re-checks the selected
                // Studio only when it actually proceeds.
                await verifiedAction.run(async () => {
                  if (selecting.current === true) return;
                  selecting.current = true;
                  try {
                    const token = await generate();
                    if (!currentConsent()) return;

                    const planMap = {
                      free: BillingRegistryPlan.PLAN_FREE,
                      standard: BillingRegistryPlan.PLAN_STANDARD,
                      pro: BillingRegistryPlan.PLAN_PRO
                    };

                    const freqMap = {
                      monthly: BillingRegistryPlanFrequency.PLAN_FREQUENCY_MONTHLY,
                      yearly: BillingRegistryPlanFrequency.PLAN_FREQUENCY_YEARLY
                    };

                    const billingPlan = planMap[plan] ?? BillingRegistryPlan.PLAN_FREE;
                    const billingFrequency =
                      freqMap[frequency] ?? BillingRegistryPlanFrequency.PLAN_FREQUENCY_MONTHLY;

                    const returnLocation = new URL(
                      Paths.room.billingReturn.resolve(
                        roomId,
                        isElectronApp() ? 'electron' : 'web'
                      ),
                      config.frontendUrl
                    );
                    // Checkout appends the payment status and session identity.
                    // The route's optional marker must not become a URL query or
                    // trailing path segment rejected by the purchase boundary.
                    returnLocation.pathname = returnLocation.pathname.replace(/\/$/, '');
                    returnLocation.search = '';
                    const returnUrl = returnLocation.href;

                    // Per ENG-3782: wrap the request and inspect the
                    // response for protobuf-shaped errors. On any failure
                    // surface a toast and KEEP the dialog open so the user
                    // can retry without losing their plan selection. Pre-fix,
                    // a backend 500 (e.g. stale subscription ID) returned
                    // resp.error populated and resp.redirectUrl empty; the
                    // dialog would close 250ms later with no visible
                    // explanation.
                    try {
                      const resp = await BillingRegistryChangePlan.request(
                        nats.cloudConnection(),
                        BillingRegistryChangeRoomPlanRequest.fromPartial({
                          plan: billingPlan,
                          frequency: billingFrequency,
                          roomID: toIdFromHex(roomId)!,
                          token: token,
                          returnUrl,
                          purchaseConfirmed: purchaseConfirmed === true,
                          expectedPriceId:
                            quote?.kind === 'future'
                              ? quote.purchase.priceId
                              : (quote?.priceId ?? ''),
                          quoteId: futureQuote?.quoteId ?? '',
                          expectedTrialEnd: futureQuote?.effectiveAt ?? 0
                        }),
                        purchaseConfirmed === true
                          ? { force: true, cacheTTL: 0, staleOnError: false }
                          : undefined
                      );
                      if (!currentConsent()) return;

                      if (
                        resp.error === undefined &&
                        resp.purchase !== undefined &&
                        store !== undefined
                      ) {
                        store.register(roomId, resp.purchase);
                        void store.resume(roomId);
                        if (selectedId.current === roomId) setOpen(false);
                        if (resp.redirectUrl !== '') ElectronAPI.openUrl(resp.redirectUrl, false);
                        return;
                      }

                      const decision = classifyBillingResponse(
                        resp,
                        'We could not start the upgrade flow. Please try again in a moment.'
                      );

                      if (decision.kind === 'error') {
                        if (futureQuote !== undefined) {
                          reviewedQuote.current = undefined;
                          setQuoteRevision((value) => value + 1);
                          setRefreshCounter((count) => count + 1);
                        }
                        post({
                          actionable: true,
                          title: 'Could not start upgrade',
                          message: decision.message,
                          severity: 'error'
                        });
                        // KEEP dialog open — user retains plan selection.
                        return;
                      }

                      store?.registerPortal(roomId, billingPlan, billingFrequency);
                      ElectronAPI.openUrl(decision.redirectUrl, false);
                      if (selectedId.current === roomId) {
                        setOpen(false);
                        setRefreshCounter((count) => count + 1);
                      }
                    } catch (err) {
                      if (!currentConsent()) return;
                      if (futureQuote !== undefined) {
                        reviewedQuote.current = undefined;
                        setQuoteRevision((value) => value + 1);
                        setRefreshCounter((count) => count + 1);
                      }
                      logger.error('plan change request rejected', err);
                      post({
                        actionable: true,
                        title: 'Could not start upgrade',
                        message: thrownRequestMessage(),
                        severity: 'error'
                      });
                    }
                  } finally {
                    selecting.current = false;
                  }
                });
              }}
            />
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
      {verifiedAction.prompt}
    </div>
  );
};
